import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { Check, ChevronsUpDown, Plus, User } from 'lucide-react'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { useStore } from '@tanstack/react-store'

import { CreateContactRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { useContacts, useCreateContact } from '@/stores/contacts.store'
import { organizationStore } from '@/stores/organization.store'
import { useCheckDuplicateContactEmail } from '@/hooks/use-check-duplicate-contact-email'
import { DuplicateContactAlert } from './duplicate-contact-alert'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ActionGate } from '@/components/shared/action-gate'
import { cn, formatFullName, stripPhoneFormatting } from '@/lib/utils'

interface ContactSelectorProps {
  value?: string
  onChange: (contactId: string) => void
  disabled?: boolean
  onNewContactClick?: () => void  // Let parent handle new contact mode
}

/**
 * ContactSelector - Unified component for selecting or creating contacts
 *
 * Features:
 * - Searchable dropdown of existing contacts
 * - Inline contact creation form
 * - Automatic switching between modes
 * - Real-time validation
 *
 * @param value - Selected contact ID
 * @param onChange - Callback when contact is selected/created
 * @param disabled - Disable the selector
 */
export function ContactSelector({ value, onChange, disabled, onNewContactClick }: ContactSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [showCreateForm, setShowCreateForm] = React.useState(false)

  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { contacts, isLoading } = useContacts()
  const { mutation: createMutation } = useCreateContact()

  // Find selected contact
  const selectedContact = contacts.find(c => c.id === value)

  // Create contact form
  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    onSubmit: async ({ value: formValue }) => {
      try {
        const firstName = formValue.firstName.trim()
        const lastName = formValue.lastName.trim()
        const email = formValue.email.trim()
        const phoneNumber = stripPhoneFormatting(formValue.phoneNumber)

        if (!firstName && !lastName && !email && !phoneNumber) {
          toast.error('Please enter at least one contact detail')
          return
        }

        const request = create(CreateContactRequestSchema, {
          organizationId: selectedOrganizationId || '',
          firstName,
          lastName,
          email: email || undefined,
          phoneNumber: phoneNumber || undefined,
        })

        const response = await createMutation.mutateAsync(request)

        if (response.contact) {
          onChange(response.contact.id)
          toast.success('Contact created successfully')
          form.reset()
          setShowCreateForm(false)
          setOpen(false)
        }
      } catch (error) {
        console.error('Failed to create contact:', error)
        reportError(error, { feature: 'contact_create', kind: 'mutation' })
        toast.error('Failed to create contact', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Check for duplicate email when creating new contact
  const { existingContact, checkEmail, clearExistingContact } = useCheckDuplicateContactEmail({
    organizationId: selectedOrganizationId,
    disabled: !showCreateForm,
  })

  // Handle using an existing contact instead of creating new
  const handleUseExistingContact = (contactId: string) => {
    onChange(contactId)
    form.reset()
    setShowCreateForm(false)
    clearExistingContact()
  }

  const getContactDisplayName = (contact: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string }) => (
    formatFullName(contact.firstName, contact.lastName, contact.email || contact.phoneNumber || 'Unknown contact')
  )

  return (
    <div className="grid gap-3">
      <Label htmlFor="contact-selector">Contact</Label>

      {!showCreateForm ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="contact-selector"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled}
            >
              {selectedContact ? (
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {getContactDisplayName(selectedContact)}
                </span>
              ) : (
                <span className="text-muted-foreground">Select contact...</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search contacts..." />
              <CommandList>
                {/* New Contact at top */}
                <CommandGroup>
                  <ActionGate
                    organizationId={selectedOrganizationId || ''}
                    permission="ContactsManage"
                    verb="create"
                    noun="contacts"
                  >
                    <CommandItem
                      onSelect={() => {
                        if (onNewContactClick) {
                          onNewContactClick()
                          setOpen(false)
                        } else {
                          setShowCreateForm(true)
                          setOpen(false)
                        }
                      }}
                      className="bg-muted/50"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      New Contact
                    </CommandItem>
                  </ActionGate>
                </CommandGroup>
                <CommandSeparator />
                <CommandEmpty>No contact found.</CommandEmpty>
                <CommandGroup heading="Contacts">
                  {isLoading ? (
                    <CommandItem disabled>Loading contacts...</CommandItem>
                  ) : (
                    contacts.map((contact) => (
                      <CommandItem
                        key={contact.id}
                        value={`${contact.firstName} ${contact.lastName} ${contact.email || ''} ${contact.phoneNumber || ''}`}
                        onSelect={() => {
                          onChange(contact.id)
                          setOpen(false)
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === contact.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span>{getContactDisplayName(contact)}</span>
                          {contact.email && (
                            <span className="text-xs text-muted-foreground">
                              {contact.email}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Create New Contact</h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                form.reset()
                setShowCreateForm(false)
              }}
            >
              Cancel
            </Button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <form.Field name="firstName">
                {(field) => (
                  <div className="grid gap-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={createMutation.isPending}
                      placeholder="John"
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="lastName">
                {(field) => (
                  <div className="grid gap-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={createMutation.isPending}
                      placeholder="Doe"
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <form.Field name="email">
              {(field) => (
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={() => {
                      field.handleBlur()
                      checkEmail(field.state.value)
                    }}
                    disabled={createMutation.isPending}
                    placeholder="john@example.com"
                  />
                </div>
              )}
            </form.Field>

            {existingContact && (
              <DuplicateContactAlert
                contact={existingContact}
                onUseExisting={handleUseExistingContact}
                disabled={createMutation.isPending}
              />
            )}

            <form.Field name="phoneNumber">
              {(field) => (
                <div className="grid gap-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={createMutation.isPending}
                    placeholder="(555) 123-4567"
                  />
                </div>
              )}
            </form.Field>

            <Button
              type="submit"
              disabled={
                createMutation.isPending ||
                (
                  !form.state.values.firstName.trim() &&
                  !form.state.values.lastName.trim() &&
                  !form.state.values.email.trim() &&
                  !stripPhoneFormatting(form.state.values.phoneNumber)
                )
              }
              className="w-full"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Contact'}
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}
